// Gemeinsamer, reaktiver Zustand der Shell (Stand, Navigation, Handy-App-Leiste, Mehr-Menü, Farbschema).
// In Komponenten als this.$shell verfügbar (src/plugins/ui.js), sonst per import { shell }.
// Startwerte aus localStorage; jeder Zugriff in try/catch (privates Fenster, gesperrter Speicher).
import Vue from 'vue';

const KEY_SIDEBAR = 'seitenleiste';
// Shell-Variante zum Vergleich: 'klassisch' (Navigation links mit Blatt) oder 'hub' (Aufbau wie der Mobility Hub)
const KEY_VARIANT = 'shellVariante';
// neuer Schlüssel: bisher wurde jede besuchte Gruppe gemerkt (früher 'navOffen'); jetzt nur noch per Klick geöffnete
const KEY_GROUPS = 'navGruppen';
// Farbschema (Kundenwunsch 12.10.2026): 'hell' (Standard) oder 'dunkel'. Es gilt nur in der Shell, die Anmeldung bleibt
// hell; angewendet wird es in src/shell/farbschema.js, vor dem ersten Rendern liest das Inline-Skript in
// public/index.html denselben Schlüssel.
const KEY_THEME = 'farbschema';
// Hauptfarbe (Kundenwunsch 13.10.2026): 'petrol' (das frühere Navy; seit 13.10.2026 Standard, Kundenwunsch „als default
// farbe bitte noch petrol nicht das blau“), 'blau' oder 'orange'; ohne gespeicherte Wahl Petrol, eine gespeicherte Wahl
// bleibt. Angewendet wie das Farbschema (src/shell/farbschema.js, Inline-Skript in public/index.html)
const KEY_HAUPTFARBE = 'hauptfarbe';
const HAUPTFARBEN = ['blau', 'petrol', 'orange'];
const HAUPTFARBE_STANDARD = 'petrol';

function readSidebarCollapsed() {
  try {
    return window.localStorage.getItem(KEY_SIDEBAR) === 'eingeklappt';
  } catch (e) {
    return false;
  }
}

function readVariant() {
  try {
    return window.localStorage.getItem(KEY_VARIANT) === 'hub' ? 'hub' : 'klassisch';
  } catch (e) {
    return 'klassisch';
  }
}

function readOpenGroups() {
  try {
    const value = JSON.parse(window.localStorage.getItem(KEY_GROUPS));
    return Array.isArray(value) ? value.filter(id => typeof id === 'string') : [];
  } catch (e) {
    return [];
  }
}

function readTheme() {
  try {
    return window.localStorage.getItem(KEY_THEME) === 'dunkel' ? 'dunkel' : 'hell';
  } catch (e) {
    return 'hell';
  }
}

function readHauptfarbe() {
  try {
    const value = window.localStorage.getItem(KEY_HAUPTFARBE);
    return HAUPTFARBEN.indexOf(value) === -1 ? HAUPTFARBE_STANDARD : value;
  } catch (e) {
    return HAUPTFARBE_STANDARD;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Speicher nicht verfügbar: Zustand gilt nur bis zum Neuladen
  }
}

export const shell = Vue.observable({
  stand: null, // Date der letzten erfolgreichen API-Antwort (axios-Interceptor in main.js), „Stand“ unten in der Navigation
  phoneAction: null, // { icon, label, handler } – höchstens eine 44-px-Aktion in der Handy-App-Leiste
  moreOpen: false, // Handy: Mehr-Menü offen
  moreGroup: null, // Handy: geöffnete zweite Ebene im Mehr-Menü (Gruppen-ID)
  sidebarCollapsed: readSidebarCollapsed(), // Maus ≥ 1264: Seitenleiste auf Rail 60 eingeklappt
  openGroups: readOpenGroups(), // per Klick geöffnete Navigationsgruppen der Seitenleiste (IDs, gemerkt)
  autoGroup: null, // Gruppe der aktiven Seite: automatisch offen, nicht gemerkt
  popupOffen: 0, // Anzahl offener Navigations-Popups (Flyout, Benutzermenü, Produktwechsel): Tooltips aus
  tenantSwitch: false, // Dialog „Mandant wechseln“ offen (nur Superadmin; Benutzermenü, Mehr-Menü, Hub-Kopfleiste)
  tenantPreselect: null, // Hub-Kopfleiste: im Mandantenmenü gewählter Mandant, der Dialog beginnt dann beim Passwort
  variant: readVariant(), // 'klassisch' | 'hub'
  hubNavOpen: false, // Hub unter 768 px: Navigation als Schublade offen
  theme: readTheme(), // Farbschema 'hell' | 'dunkel' (gewählt; MainLayout wendet es über src/shell/farbschema.js an)
  hauptfarbe: readHauptfarbe(), // 'blau' | 'petrol' | 'orange' (ebenso)
  hauptfarbeFrei: false // Hauptfarbe wählbar (nur bconn_admin, MainLayout); sonst gilt Petrol
});

export function setVariant(value) {
  shell.variant = value === 'hub' ? 'hub' : 'klassisch';
  write(KEY_VARIANT, shell.variant);
}

export function setTheme(value) {
  shell.theme = value === 'dunkel' ? 'dunkel' : 'hell';
  write(KEY_THEME, shell.theme);
}

export function setHauptfarbe(value) {
  shell.hauptfarbe = HAUPTFARBEN.indexOf(value) === -1 ? HAUPTFARBE_STANDARD : value;
  write(KEY_HAUPTFARBE, shell.hauptfarbe);
}

// Wahl aus einem anderen Tab desselben Browsers übernehmen (storage-Ereignis; key null: Speicher geleert)
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', event => {
    if (event.key === KEY_THEME || event.key === null) {
      shell.theme = readTheme();
    }
    if (event.key === KEY_HAUPTFARBE || event.key === null) {
      shell.hauptfarbe = readHauptfarbe();
    }
  });
}

export function setPhoneAction(action) {
  shell.phoneAction = action || null;
}

export function setSidebarCollapsed(value) {
  shell.sidebarCollapsed = !!value;
  write(KEY_SIDEBAR, value ? 'eingeklappt' : 'ausgeklappt');
}

export function isGroupOpen(id) {
  return shell.openGroups.indexOf(id) !== -1 || (!!id && shell.autoGroup === id);
}

function setOpenGroups(list) {
  shell.openGroups = list;
  write(KEY_GROUPS, JSON.stringify(list));
}

export function openGroup(id) {
  if (!id || shell.openGroups.indexOf(id) !== -1) {
    return;
  }
  setOpenGroups(shell.openGroups.concat([id]));
}

export function closeGroup(id) {
  if (shell.autoGroup === id) {
    shell.autoGroup = null;
  }
  if (shell.openGroups.indexOf(id) !== -1) {
    setOpenGroups(shell.openGroups.filter(g => g !== id));
  }
}

export function toggleGroup(id) {
  if (isGroupOpen(id)) {
    closeGroup(id);
  } else {
    openGroup(id);
  }
}

// Gruppe der aktiven Seite (bei jedem Routenwechsel): öffnet automatisch, ohne gemerkt zu werden
export function setAutoGroup(id) {
  shell.autoGroup = id || null;
}

// Navigations-Popups melden sich an und ab; solange eines offen ist, zeigen die Rail-Tooltips nichts
export function notePopup(open) {
  shell.popupOffen = Math.max(0, shell.popupOffen + (open ? 1 : -1));
}

// Die Funktionen hängen zusätzlich (nicht aufzählbar, nicht reaktiv) am Objekt, damit Vorlagen
// $shell.setSidebarCollapsed(true), $shell.toggleGroup(id) usw. direkt aufrufen können.
Object.defineProperties(shell, {
  setPhoneAction: { value: setPhoneAction },
  setVariant: { value: setVariant },
  setTheme: { value: setTheme },
  setHauptfarbe: { value: setHauptfarbe },
  setSidebarCollapsed: { value: setSidebarCollapsed },
  isGroupOpen: { value: isGroupOpen },
  openGroup: { value: openGroup },
  closeGroup: { value: closeGroup },
  toggleGroup: { value: toggleGroup },
  setAutoGroup: { value: setAutoGroup },
  notePopup: { value: notePopup }
});
